<template>
  <aside id="ivr-config-drawer">
    <header v-if="nodeDetails">
      <h2>Configurar {{ getNodeText(nodeDetails, parentNodeDetails).complete }}</h2>

      <a-button type="text" class="button button__close" @click="handleClose">
        <CloseOutlined style="font-size: 16px" />
      </a-button>
    </header>

    <CustomScrollbar height="100%" width="100%">
      <a-tooltip :title="disabled ? 'Desactiva el bot para editar' : null">
        <section>
          <a-spin :spinning="loading">
            <p>Completa los campos requeridos</p>
            <component
              v-if="nodeDetails"
              ref="formContentRef"
              :is="ContentPerType[nodeDetails.type]"
              :node="nodeDetails"
              :parentNode="parentNodeDetails"
              :disabled="disabled"
              :optionsConnectorApi="optionsConnectorApi"
            />
          </a-spin>
        </section>
      </a-tooltip>
    </CustomScrollbar>

    <DisabledContainer :isDisabled="disabled">
      <footer>
        <a-button @click="handleClose">Cancelar</a-button>
        <a-button
          type="primary"
          id="ivr-drawer-primary"
          @click="handleSave"
          :loading="isSaving"
          :disabled="isSaveDisabled"
        >
          Guardar
        </a-button>
      </footer>
    </DisabledContainer>
  </aside>
</template>

<script setup lang="ts">
import { computed, createVNode, inject, onMounted, ref } from "vue";
import { CloseOutlined, QuestionCircleOutlined } from "@ant-design/icons-vue";
import { cloneDeep, isEqual } from "lodash";
import { message, Modal } from "ant-design-vue";

import { CurrentOpenedNodeKey, FetchNodesKey } from "@/@types/ivr/symbols";
import { nodeServices } from "@/app/ivr/bot/services/NodeServices";
import { ActionType, NodeType } from "@/app/ivr/bot/utils/enums";
import { Connector, CreateNodeBody, Node } from "@/@types/ivr";
import { getNodeText } from "@/app/ivr/bot/utils/texts";
import { ResponseError } from "@/app/shared/utils/httpManager";

import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import NumericDialing from "@/app/ivr/bot/components/drawer/content/NumericDialing.vue";
import Option from "@/app/ivr/bot/components/drawer/content/Option.vue";
import WrongOption from "@/app/ivr/bot/components/drawer/content/WrongOption.vue";
import VariableCapture from "@/app/ivr/bot/components/drawer/content/VariableCapture.vue";
import SatisfactionRanking from "@/app/ivr/bot/components/drawer/content/SatisfactionRanking.vue";
import ExpiredTime from "@/app/ivr/bot/components/drawer/content/ExpiredTime.vue";
import DisabledContainer from "@/app/shared/components/molecules/DisabledContainer.vue";
import { connectorServices } from "../../services/ConnectorServices";

type DynamicComponent =
  | typeof NumericDialing
  | typeof Option
  | typeof WrongOption
  | typeof VariableCapture
  | typeof ExpiredTime
  | typeof SatisfactionRanking;

const ContentPerType: Record<NodeType, DynamicComponent> = {
  [NodeType.NumericDialing]: NumericDialing,
  [NodeType.Option]: Option,
  [NodeType.WrongOption]: WrongOption,
  [NodeType.VariableCapture]: VariableCapture,
  [NodeType.ExpiredTime]: ExpiredTime,
  [NodeType.SatisfactionRanking]: SatisfactionRanking,
  [NodeType.WrongOptionApi]: WrongOption,
  [NodeType.OptionConsultApi]: Option,
};

const props = defineProps<{
  visible: boolean;
  nodeId: number | undefined;
  disabled?: boolean;
  deletedQueues?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:visible", visible: boolean): void;
  (e: "refreshBot"): void;
}>();

const formContentRef = ref<InstanceType<DynamicComponent>>();

const currentOpenedNode = inject(CurrentOpenedNodeKey);
const fetchNodes = inject(FetchNodesKey);

const loading = ref(false);
const nodeDetails = ref<Node>();
const parentNodeDetails = ref<Node>();

const fetchNodeDetails = async () => {
  if (!props.nodeId) return;

  loading.value = true;

  nodeDetails.value = await nodeServices.getDetails(props.nodeId);

  const parentNodeId = nodeDetails.value?.node;
  if (parentNodeId) parentNodeDetails.value = await nodeServices.getDetails(parentNodeId);

  loading.value = false;
};

const comparisonForm = ref<Partial<CreateNodeBody>>();

onMounted(async () => {
  await fetchNodeDetails();
  comparisonForm.value = cloneDeep(formContentRef.value?.formState);
});

const isSaveDisabled = computed(() =>
  isEqual(comparisonForm.value, formContentRef.value?.formState)
);

const closeDrawer = () => {
  if (!currentOpenedNode?.value) return;

  emit("update:visible", false);
  currentOpenedNode.value = undefined;
};

const handleClose = async () => {
  if (isSaveDisabled.value) {
    closeDrawer();
    return;
  }

  await new Promise((res) => {
    Modal.confirm({
      icon: createVNode(QuestionCircleOutlined),
      centered: true,
      title: "¿Deseas descartar los cambios realizados?",
      content: "Recuerda que si no guardas los cambios registrados, estos se perderán.",
      okText: "Sí, descartar cambios",
      cancelText: "No, seguir editando",
      onOk: () => {
        closeDrawer();
        res(undefined);
      },
    });
  });
};

const isSaving = ref(false);

const handleSave = async () => {
  await formContentRef.value?.formRef?.validate();

  isSaving.value = true;

  try {
    const newNode = formContentRef.value?.formState;
    if (!nodeDetails?.value || !newNode) return;

    console.log(newNode, 'new node');
    
    let optionConnector = null;
    if (newNode.action == ActionType.ConnectorApi && newNode.api_connector)
      optionConnector = optionsConnectorApi.value.find(el => el.id == newNode.api_connector);

    if (newNode.api_connector != nodeDetails?.value.api_connector?.id && newNode.action == ActionType.ConnectorApi) {
      Modal.confirm({
        title: "¿Estás seguro de editar este conector de API?",
        content: "Recuerda que al guardar los cambios se perderán todas las configuraciones en los IVR Operadores Automáticos configurados.",
        icon: createVNode(QuestionCircleOutlined),
        centered: true,
        okText: "Sí, guardar cambios",
        cancelText: "No, seguir editando",
        async onOk() {
          // console.log(optionConnector, 'optionConnector')
          await nodeServices.update(nodeDetails.value, newNode, { preventDefault: false }, optionConnector?.data_value);

          message.success("Se guardaron los cambios");

          fetchNodes?.();

          closeDrawer();
        },
      });
    } else {
      // console.log(optionConnector, 'optionConnector')
      await nodeServices.update(nodeDetails.value, newNode, { preventDefault: false }, optionConnector?.data_value);

      message.success("Se guardaron los cambios");

      fetchNodes?.();

      closeDrawer();
    }

    if (props.deletedQueues)
      emit("refreshBot");
  } catch (error) {
    const errorMessage =
      error instanceof ResponseError ? error.getErrorMessage() : "Ha ocurrido un error";

    message.error(errorMessage);
  }

  isSaving.value = false;
};

const optionsConnectorApi = ref<Connector[]>([]);

const fetchConnectorList = async () => {
  try {
    // loading.value = true;
    const response = await connectorServices.getList();
    optionsConnectorApi.value = response.map((el) => ({ ...el, value: el.id, label: el.name }));
  } catch (error) {
    console.log(error);
  } finally {
    // loading.value = false;
  }
};

onMounted(() => {
  fetchConnectorList();
});

defineExpose({ handleClose });
</script>

<style scoped lang="sass">
aside
  width: 460px
  background-color: white
  border-top-left-radius: 10px
  border-top: 1px solid $gray_dark_100
  border-left: 1px solid $gray_dark_100
  display: grid
  grid-template-rows: auto 1fr auto

header
  display: flex
  justify-content: space-between
  align-items: center
  padding: 12px 16px
  border-bottom: 1px solid $gray_dark_100

h2
  font-weight: 600
  font-size: 14px
  color: $gray-9
  line-height: 20px
  margin: 0

p
  margin-bottom: 8px
  color: $gray-8

.button
  padding: 0

  &__close
    height: 20px

section
  padding: 12px 16px
  overflow-y: auto

footer
  border-top: 1px solid $gray_dark_100
  padding: 12px 16px
  display: flex
  justify-content: flex-end
  gap: 10px
</style>
